import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useIdentity } from '@zenbusiness/application-commons-identity';

import { useAccountSettings } from '#client/hooks/useAccountSettings';
import useEmailVerified from '#client/hooks/useEmailVerified';
import { useEnterprise } from '#client/hooks/useEnterprise';
import { isSocialAuth0Id } from '#client/utils/isSocialAuth0Id';

/**
 * Email verification state for the home banner and related UI.
 * `emailVerified` is tri-state: `true`, `false`, or `null` (unknown).
 * The banner is for authenticated, non-enterprise, non-social-login customers whose email is known unverified.
 * Email comes from the identity user record (account email), not the session JWT/cookie,
 * so an OBO session never interpolates the employee's address.
 *
 * @returns {{ emailVerified: boolean|null, email: string|null, shouldShowBanner: boolean }}
 */
export function useEmailVerification() {
  const { authenticated } = useAuthentication() || {};
  const { accountEmail } = useIdentity() || {};
  const emailVerified = useEmailVerified();
  const { enterprise, loading } = useEnterprise();
  const { data: accountInfo, loading: accountLoading, error: accountError } = useAccountSettings();
  const email = typeof accountEmail === 'string' && accountEmail ? accountEmail : null;

  const shouldShowBanner =
    Boolean(authenticated) &&
    emailVerified === false &&
    !enterprise &&
    !loading &&
    !accountLoading &&
    !accountError &&
    !isSocialAuth0Id(accountInfo.userAuth0Id);

  return {
    emailVerified,
    email,
    shouldShowBanner,
  };
}
